import { nanoid } from 'nanoid';
import { db } from '@/core/database/indexDB/database';
import { now } from '@/lib/utils';
import pageRepository, { getPageMetadataProps } from '@/features/pages/repositories/PageRepository';
import { Collection, Section } from '../types/collection.types';

export class CollectionRepository {
  async getCollectionsSince(syncTime: number | null) {
    const collections = await db.collections
      .where('updatedAt')
      .above(syncTime || 0)
      .toArray();

    return collections.map(n => ({
      ...n,
      createdAt: ((n.createdAt / 1000) | 0) * 1000,
      updatedAt: ((n.updatedAt / 1000) | 0) * 1000,
      archivedAt: n.archivedAt ? ((n.archivedAt / 1000) | 0) * 1000 : 0,
    }));
  }

  async getCollectionById(id: string): Promise<Collection | undefined> {
    return db.collections.get(id);
  }

  async getCollections(archived = false): Promise<Collection[]> {
    if (archived) {
      return db.collections.where('archivedAt').notEqual(0).reverse().sortBy('updatedAt');
    }
    return db.collections.where('archivedAt').equals(0).reverse().sortBy('updatedAt');
  }

  async addCollection(): Promise<Collection> {
    const collectionId = nanoid();
    const sectionId = nanoid();
    const sections = [{ id: sectionId, title: 'Section 1', orderNumber: 0 }] as Section[];
    const newCollection: Collection = {
      id: collectionId,
      title: 'Untitled Collection',
      createdAt: now(),
      updatedAt: now(),
      archivedAt: 0,
      sections,
    };
    await db.collections.add(newCollection);
    await pageRepository.addPage({ collectionId, sectionId, orderNumber: 0 });
    return newCollection;
  }

  async archiveCollectionData(collection: Collection, sectionId?: string) {
    const archivedAt = now();
    const pages = await this.getCollectionPageMetadata(collection.id, sectionId);
    await pageRepository.archivePages(
      pages.map(page => page.id),
      archivedAt
    );

    const collectionUpdates: Partial<Collection> = { updatedAt: archivedAt };
    if (!sectionId) {
      collectionUpdates.archivedAt = archivedAt;
    }
    await db.collections.update(collection.id, collectionUpdates);
  }

  async unarchiveCollectionData(collection: Collection, timeWindowMs: number = 1000) {
    if (!collection.archivedAt) return;

    // Get all archived pages for this collection
    const archivedPages = await pageRepository.getArchivedPages(collection.id);

    // Find pages that were archived within the time window of the collection's archival
    const relatedPages = archivedPages.filter(
      page => page.archivedAt && Math.abs(page.archivedAt - collection.archivedAt) <= timeWindowMs
    );

    await pageRepository.unarchivePages(relatedPages.map(page => page.id));

    await db.collections.update(collection.id, {
      archivedAt: 0,
      updatedAt: now(),
    });

    return collection;
  }

  async getCollectionPageMetadata(collectionId: string, sectionId?: string) {
    const collection = await this.getCollectionById(collectionId);
    if (!collection) return [];
    const query = db.pagesMetadata
      .where('collectionId')
      .equals(collectionId)
      .and(page => (!collection.archivedAt ? !page.archivedAt : !!page.archivedAt));

    if (sectionId) {
      query.and(page => page.sectionId === sectionId);
    }

    const pages = await query.sortBy('orderNumber');
    return pages.map(getPageMetadataProps);
  }

  searchCollections(searchQuery: string) {
    return db.collections
      .where('archivedAt')
      .equals(0)
      .and(collection => collection.title.toLowerCase().includes(searchQuery.toLowerCase()))
      .reverse()
      .sortBy('updatedAt');
  }

  updateCollection(collectionId: string, collection: Partial<Collection>) {
    return db.collections.update(collectionId, { ...collection, updatedAt: now() });
  }

  bulkUpsertCollections(collections: Collection[]) {
    return db.collections.bulkPut(collections);
  }

  bulkDeleteCollections(collections: Collection[]) {
    return db.collections.bulkDelete(collections.map(c => c.id));
  }

  async getFavouritedCollections(): Promise<Collection[]> {
    const collections = await db.collections.where('archivedAt').equals(0).toArray();

    return collections
      .filter(c => c.isFavourite === true)
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async getPublicCollections(): Promise<Collection[]> {
    const collections = await db.collections.where('archivedAt').equals(0).toArray();

    return collections.filter(c => c.isPublic === true).sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async toggleProperty(collectionId: string, property: 'isFavourite' | 'isPublic') {
    const collection = await this.getCollectionById(collectionId);
    if (!collection) return;

    return this.updateCollection(collectionId, {
      [property]: !collection[property],
      updatedAt: now(),
    });
  }

  searchTaggedCollections(tag: string) {
    return db.collections.where('tags').anyOf([tag]).reverse().sortBy('updatedAt');
  }
}

export default new CollectionRepository();

export const getSectionProperties = ({ id, orderNumber, archivedAt, title }: Section) => ({
  id,
  orderNumber,
  archivedAt,
  title,
});
